import * as THREE from 'three';
import type { PlayerController } from './PlayerController';

const v = new THREE.Vector3();
const w = new THREE.Vector3();

export type CameraShot = { target: THREE.Vector3; back: THREE.Vector3; distance: number; pitch: number };

/**
 * Third-person camera on a round world. It keeps a "back" direction in the
 * player's tangent plane (carried along as you walk around the planet),
 * drifts behind you while you move, and can be dragged to orbit.
 */
export class FollowCamera {
  readonly back = new THREE.Vector3(0, 0, -1);
  pitch = 0.4;
  distance = 6.4;
  private currentDistance = 6.4;
  private readonly target = new THREE.Vector3();
  private readonly lastUp = new THREE.Vector3(0, 1, 0);
  private shot: CameraShot | null = null;
  private shotBlend = 0;
  private dragging = false;
  private idleTime = 0;

  constructor(readonly camera: THREE.PerspectiveCamera) {}

  /** Point the camera behind a facing direction instantly. */
  snapBehind(facing: THREE.Vector3): void {
    this.back.copy(facing).negate();
  }

  setShot(shot: CameraShot | null): void {
    this.shot = shot;
  }

  /** Forward direction for movement input. */
  forward(out: THREE.Vector3): THREE.Vector3 {
    return out.copy(this.back).negate();
  }

  update(dt: number, playerPos: THREE.Vector3, player: PlayerController, moving: boolean): void {
    const up = w.copy(playerPos).normalize();
    // Parallel-transport the back vector as "up" rotates around the planet.
    const q = new THREE.Quaternion().setFromUnitVectors(this.lastUp, up);
    this.back.applyQuaternion(q).projectOnPlane(up).normalize();
    this.lastUp.copy(up);

    // Ease in behind the player when they walk, unless the user is steering the camera.
    this.idleTime = moving ? 0 : this.idleTime + dt;
    if (moving && !this.dragging) {
      const behind = v.copy(player.facing).negate();
      const k = 1 - Math.exp(-dt * 1.4 * (0.5 + 0.5 * (1 - behind.dot(this.back))));
      if (behind.dot(this.back) > -0.6) this.back.lerp(behind, k).projectOnPlane(up).normalize();
    }

    this.target.lerp(v.copy(playerPos).addScaledVector(up, 1.05), 1 - Math.exp(-dt * 14));

    // Pull in when terrain or trees block the view; ease back out after.
    const dir = this.orbitDir(up, this.back, this.pitch);
    const hit = player.raycast(this.target, dir, this.distance);
    const want = hit !== null ? Math.max(2.4, hit * 0.85) : this.distance;
    const rate = want < this.currentDistance ? 18 : 2.5;
    this.currentDistance += (want - this.currentDistance) * (1 - Math.exp(-dt * rate));

    const pos = v.copy(this.target).addScaledVector(dir, this.currentDistance);
    const look = this.target.clone();

    this.shotBlend += ((this.shot ? 1 : 0) - this.shotBlend) * (1 - Math.exp(-dt * 2.6));
    if (this.shot && this.shotBlend > 0.001) {
      const s = this.shot;
      // Try a few angles around the pair and keep the one with the clearest view.
      let best = { dir: this.orbitDir(up, s.back, s.pitch), dist: -1 };
      for (const turn of [0, 0.6, -0.6, 1.2, -1.2, Math.PI]) {
        const back = s.back.clone().applyAxisAngle(up, turn);
        const dir = this.orbitDir(up, back, s.pitch + 0.1);
        const hit = player.raycast(s.target, dir, s.distance);
        const clear = hit ?? s.distance + 1;
        if (clear > best.dist) best = { dir, dist: clear };
        if (hit === null) break;
      }
      const sdir = best.dir;
      const spos = s.target.clone().addScaledVector(sdir, Math.min(s.distance, best.dist * 0.85));
      pos.lerp(spos, this.shotBlend);
      look.lerp(s.target, this.shotBlend);
    }

    this.camera.position.copy(pos);
    this.camera.up.copy(up);
    this.camera.lookAt(look);
  }

  private orbitDir(up: THREE.Vector3, back: THREE.Vector3, pitch: number): THREE.Vector3 {
    return new THREE.Vector3().copy(back).multiplyScalar(Math.cos(pitch)).addScaledVector(up, Math.sin(pitch)).normalize();
  }

  /** Orbit by a pointer drag, in pixels. */
  orbit(dx: number, dy: number): void {
    this.back.applyAxisAngle(this.lastUp, -dx * 0.006).normalize();
    this.pitch = THREE.MathUtils.clamp(this.pitch + dy * 0.004, -0.05, 1.2);
  }

  zoom(delta: number): void {
    this.distance = THREE.MathUtils.clamp(this.distance + delta * 0.004, 3, 9);
  }

  set steering(v: boolean) {
    this.dragging = v;
  }

  dispose(): void {}
}
